import React from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import {
  LayoutDashboard, TrendingUp, FileCheck, FileText, BookOpen, BarChart3,
  Users, FileSignature, ShieldAlert, UserCheck, Moon, Sun, Globe, LogOut, Landmark, Plug, ClipboardList, SquareKanban, LayoutGrid, KeyRound, Mail, Files, CalendarDays,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { NotificationBell } from "./NotificationBell";

const NAV = [
  { section: "nav.overview", items: [
    { to: "/dashboard", icon: LayoutDashboard, label: "nav.dashboard", testid: "nav-dashboard" },
    { to: "/cashflow", icon: TrendingUp, label: "nav.cashflow", testid: "nav-cashflow" },
    { to: "/tasks", icon: SquareKanban, label: "nav.task", testid: "nav-tasks" },
    { to: "/boards", icon: LayoutGrid, label: "nav.boards", testid: "nav-boards" },
    { to: "/calendar", icon: CalendarDays, label: "nav.calendar", testid: "nav-calendar" },
  ]},
  { section: "nav.accounting", items: [
    { to: "/invoices", icon: FileCheck, label: "nav.sales", testid: "nav-sales" },
    { to: "/purchases", icon: FileText, label: "nav.purchases", testid: "nav-purchases" },
    { to: "/contacts", icon: Users, label: "nav.contacts", testid: "nav-contacts" },
    { to: "/services", icon: ClipboardList, label: "nav.services", testid: "nav-services" },
    { to: "/ledger", icon: BookOpen, label: "nav.ledger", testid: "nav-ledger" },
  ]},
  { section: "nav.reportsGroup", items: [
    { to: "/reports", icon: BarChart3, label: "nav.reports", testid: "nav-reports" },
  ]},
  { section: "nav.legal", items: [
    { to: "/contracts", icon: FileSignature, label: "nav.contracts", testid: "nav-contracts" },
    { to: "/forms", icon: Files, label: "nav.forms", testid: "nav-forms" },
    { to: "/compliance", icon: ShieldAlert, label: "nav.compliance", testid: "nav-compliance" },
    { to: "/vault", icon: UserCheck, label: "nav.vault", testid: "nav-vault" },
  ]},
  { section: "nav.integrations", items: [
    { to: "/connectors", icon: Plug, label: "nav.connectors", testid: "nav-connectors" },
    { to: "/emails", icon: Mail, label: "nav.emails", testid: "nav-emails" },
    { to: "/users", icon: KeyRound, label: "nav.users", testid: "nav-users" },
  ]},
];

export default function Layout() {
  const { t, entities, entity, setEntity, lang, setLang, theme, setTheme, user, logout } = useApp();
  const navigate = useNavigate();
  const isViewer = user?.role === "viewer";
  const nav = isViewer
    ? [{ section: "nav.overview", items: NAV[0].items.filter((i) => ["/tasks", "/boards", "/calendar"].includes(i.to)) }]
    : NAV;

  return (
    <div className="min-h-screen flex" data-testid="app-layout">
      <aside className="w-64 shrink-0 border-r border-border bg-card hidden md:flex flex-col sticky top-0 h-screen overflow-y-auto">
        <div className="flex items-center gap-3 px-5 py-5 border-b border-border">
          <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center">
            <Landmark className="h-5 w-5 text-primary-foreground" />
          </div>
          <div>
            <div className="font-heading font-bold text-sm leading-tight">{t("app.name")}</div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("app.tagline")}</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-5">
          {nav.map((sec) => (
            <div key={sec.section}>
              <div className="px-2 mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t(sec.section)}</div>
              <div className="space-y-0.5">
                {sec.items.map((item) => (
                  <NavLink key={item.to} to={item.to} data-testid={item.testid}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm transition-colors duration-150 ${
                        isActive ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:bg-accent hover:text-foreground"}`}>
                    <item.icon className="h-4 w-4" />
                    {t(item.label)}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border">
          <div className="flex items-center gap-3 px-4 sm:px-6 h-16">
            <Select value={entity} onValueChange={setEntity}>
              <SelectTrigger className="w-[130px] sm:w-[240px]" data-testid="entity-selector-dropdown">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {entities.map((e) => (
                  <SelectItem key={e.entity_id} value={e.entity_id} data-testid={`entity-option-${e.entity_id}`}>
                    <span className="font-mono text-xs mr-2 text-muted-foreground">{e.code}</span>
                    {lang === "it" ? e.name_it : e.name_en}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex-1" />
            <NotificationBell />
            <Button variant="ghost" size="sm" data-testid="language-toggle-button"
              onClick={() => setLang(lang === "it" ? "en" : "it")}>
              <Globe className="h-4 w-4 mr-1.5" />{lang === "it" ? "IT" : "EN"}
            </Button>
            <Button variant="ghost" size="icon" data-testid="theme-toggle-button"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-button" className="rounded-full">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback>{(user?.name || "U")[0]}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <div className="px-2 py-1.5 text-sm">
                  <div className="font-medium">{user?.name}</div>
                  <div className="text-xs text-muted-foreground">{user?.email}</div>
                </div>
                <DropdownMenuItem data-testid="logout-button" onClick={async () => { await logout(); navigate("/login"); }}>
                  <LogOut className="h-4 w-4 mr-2" />Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
